import { useEffect, useState } from 'react'

import { cn } from '#/shared/lib/utils'

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg'

interface AvatarProps {
  src?: string
  name: string
  size?: AvatarSize
  className?: string
}

const sizeClasses: Record<AvatarSize, string> = {
  xs: 'size-5 text-2xs',
  sm: 'size-6 text-xs',
  md: 'size-8 text-sm',
  lg: 'size-10 text-base',
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

export function Avatar({ src, name, size = 'sm', className }: AvatarProps) {
  const sizeCn = sizeClasses[size]
  const [loaded, setLoaded] = useState(false)
  const [errored, setErrored] = useState(false)

  // Restart the load/error cycle whenever the source changes (e.g. reassigning
  // the owner) so a new picture never inherits the previous one's state.
  useEffect(() => {
    setLoaded(false)
    setErrored(false)
  }, [src])

  // A broken link falls back to initials; the placeholder pulses until it loads.
  const showImage = src != null && src !== '' && !errored
  const loading = showImage && !loaded

  return (
    <span
      aria-label={name}
      className={cn(
        'relative flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-200 font-semibold text-gray-700 select-none',
        sizeCn,
        loading && 'animate-pulse',
        className,
      )}
    >
      {!loaded && getInitials(name)}
      {showImage && (
        <img
          src={src}
          alt=""
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
          className={cn(
            'absolute inset-0 size-full rounded-full object-cover transition-opacity duration-200',
            loaded ? 'opacity-100' : 'opacity-0',
          )}
        />
      )}
    </span>
  )
}

export type { AvatarProps, AvatarSize }
